import { useState } from "react";
import { Link } from "wouter";
import { useListStatements } from "@workspace/api-client-react";
import { useStatementUpload, useDeleteStatement } from "@/hooks/use-uploads";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDropzone } from "@/components/file-dropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, safeDateFormat } from "@/lib/utils";
import { FileText, Plus, ChevronRight, Download, Trash2 } from "lucide-react";

export default function Statements() {
  const { data: statements, isLoading } = useListStatements();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const uploadMutation = useStatementUpload();
  const deleteMutation = useDeleteStatement();

  const handleUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    await uploadMutation.mutateAsync(file);
    setIsUploadOpen(false);
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Statements</h1>
          <p className="text-slate-500 mt-1">Upload and manage your credit card statements.</p>
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Upload Statement
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Filename</th>
                <th className="px-6 py-4">Statement Date</th>
                <th className="px-6 py-4">Total Spending</th>
                <th className="px-6 py-4">Match Progress</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {statements?.map((statement) => {
                const matchPercent = statement.totalEntries > 0 
                  ? Math.round((statement.matchedEntries / statement.totalEntries) * 100) 
                  : 0;
                  
                return (
                  <tr key={statement.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-primary" />
                        <span className="font-medium text-slate-900">{statement.originalFilename}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {safeDateFormat(statement.statementDate)}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {formatCurrency(statement.totalSpending || 0)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-full bg-slate-200 rounded-full h-2 max-w-[100px]">
                          <div 
                            className={`h-2 rounded-full ${matchPercent === 100 ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${matchPercent}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{statement.matchedEntries}/{statement.totalEntries}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {matchPercent === 100 ? (
                        <Badge variant="success">Fully Matched</Badge>
                      ) : (
                        <Badge variant="warning">Incomplete</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a 
                          href={`/api/statements/${statement.id}/download`} 
                          target="_blank"
                          className="p-2 text-slate-400 hover:text-primary transition-colors"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete "${statement.originalFilename}"? This will remove the statement and all its entries. Any matched receipts will become unmatched.`)) {
                              deleteMutation.mutate(statement.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete statement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <Link href={`/statements/${statement.id}`}>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            View Details <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
              
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500">Loading statements...</td>
                </tr>
              )}
              
              {!isLoading && statements?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p>No statements uploaded yet.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isUploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display font-bold">Upload Statement</h2>
              <button onClick={() => setIsUploadOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <FileDropzone onUpload={handleUpload} accept=".pdf,.xls,.xlsx,.csv" label="Drop credit card statement (PDF, Excel, or CSV) here" />
          </div>
        </div>
      )}
    </Layout>
  );
}

// Inline X icon for modal
function X(props: any) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
}
